import { useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from "lodash";

import {
  Box,
  Checkbox,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@mui/icons-material/Search";

import InputText from "components/_refactor/text/input";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import RepresentativeCard from "./representative-card";
import ListPagination from "./list-pagination";
import SelectionScopeModal from "./selection-scope-modal";
import {
  REPRESENTATIVE_PAGE_SIZE,
  SELECT_ALL_REPRESENTATIVES_MAX,
} from "../utils/representative-helpers";

export default function RepresentativeSelectionList({
  isLoading,
  items = [],
  totalItems = 0,
  page = 0,
  totalPages = 0,
  pageSize = REPRESENTATIVE_PAGE_SIZE,
  onPageChange,
  selectedIds,
  onToggleItem,
  onToggleAllVisible,
  onSelectAllResults,
  allVisibleSelected,
  someVisibleSelected,
  searchValue,
  onSearchChange,
  emptyMessage = "Nenhum registro encontrado com os filtros aplicados.",
  selectionFooter,
  selectAllDialogTitle = "Selecionar registros",
  selectAllEntityLabel = "registros",
}) {
  const [openSelectAllDialog, setOpenSelectAllDialog] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const visibleIds = useMemo(
    () => items.map((item) => item.__selectionId),
    [items],
  );

  const handleHeaderSelectClick = useCallback(() => {
    if (allVisibleSelected) {
      onToggleAllVisible(visibleIds);
      return;
    }
    setOpenSelectAllDialog(true);
  }, [allVisibleSelected, onToggleAllVisible, visibleIds]);

  const handleSelectAllCurrentPage = useCallback(() => {
    if (!allVisibleSelected) {
      onToggleAllVisible(visibleIds);
    }
    setOpenSelectAllDialog(false);
  }, [allVisibleSelected, onToggleAllVisible, visibleIds]);

  const handleSelectAllResults = useCallback(async () => {
    if (!onSelectAllResults) {
      handleSelectAllCurrentPage();
      return;
    }

    setSelectAllLoading(true);
    try {
      await onSelectAllResults();
      setOpenSelectAllDialog(false);
    } finally {
      setSelectAllLoading(false);
    }
  }, [handleSelectAllCurrentPage, onSelectAllResults]);

  return (
    <Paper
      variant="outlined"
      style={{
        marginTop: 24,
        borderColor: themeColorsDesingSystem.stone.main,
        overflow: "hidden",
      }}
    >
      <Box
        px={2}
        py={1.5}
        borderBottom={`1px solid ${themeColorsDesingSystem.stone.main}`}
      >
        <InputText
          placeholder="Pesquisar..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          disabled={isLoading}
          startAdornment={
            <SearchIcon
              fontSize="small"
              style={{ color: themeColorsDesingSystem.gray.dark }}
            />
          }
        />
      </Box>

      <Box
        style={{
          minHeight: 280,
          maxHeight: 420,
          overflowY: "auto",
          padding: "4px 8px",
          backgroundColor: themeColorsDesingSystem.background.main,
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress size={32} />
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              alignItems="center"
              py={1}
              px={1}
              style={{
                borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
              }}
            >
              <Checkbox
                checked={allVisibleSelected}
                indeterminate={someVisibleSelected && !allVisibleSelected}
                onChange={handleHeaderSelectClick}
                disabled={items.length === 0}
                color="primary"
                size="small"
              />
              <Typography
                variant="subtitle2"
                style={{
                  margin: 0,
                  fontWeight: 700,
                  color: themeColorsDesingSystem.primary.main,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                Todos
              </Typography>
            </Box>

            {items.length === 0 ? (
              <Box py={4} px={2} textAlign="center">
                <Typography variant="body2" color="textSecondary">
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
              items.map((item) => (
                <RepresentativeCard
                  key={item.__selectionId}
                  item={item}
                  checked={selectedIds.has(item.__selectionId)}
                  onChange={() => onToggleItem(item)}
                  disabled={isLoading}
                />
              ))
            )}
          </>
        )}
      </Box>

      <Box px={2} pb={1}>
        <ListPagination
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={onPageChange}
        />
      </Box>

      {selectionFooter}

      <SelectionScopeModal
        open={openSelectAllDialog}
        onClose={() =>
          selectAllLoading ? undefined : setOpenSelectAllDialog(false)
        }
        title={selectAllDialogTitle}
        entityLabel={selectAllEntityLabel}
        currentPageCount={items.length}
        totalCount={totalItems}
        maxBulkSelect={SELECT_ALL_REPRESENTATIVES_MAX}
        loading={selectAllLoading}
        onCancel={() => setOpenSelectAllDialog(false)}
        onSelectCurrentPage={handleSelectAllCurrentPage}
        onSelectAllResults={handleSelectAllResults}
        showSelectAllResults={Boolean(onSelectAllResults)}
      />
    </Paper>
  );
}

export function useRepresentativeListSearch(initialSearch = "", onDebouncedSearch) {
  const [searchInput, setSearchInput] = useState(initialSearch);

  const debounced = useMemo(
    () => debounce((value) => onDebouncedSearch(value), 300),
    [onDebouncedSearch],
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  const handleSearchChange = useCallback(
    (value) => {
      setSearchInput(value);
      debounced(value);
    },
    [debounced],
  );

  return { searchInput, handleSearchChange, setSearchInput };
}
